<template>
  <form
    class="relative flex min-h-full min-w-full grow flex-col"
    @submit="onSubmit"
  >
    <div class="flex items-start justify-between space-x-2">
      <BaseCard class="flex-1 space-y-6">
        <div
          class="col-span-4 flex w-full items-center justify-center"
        >
          <div
            class="relative flex size-12 items-center justify-center rounded bg-primary text-white"
            :style="{
              backgroundColor:
                productPreviewColor || undefined,
            }"
          >
            {{ productPreviewShortString }}
          </div>
        </div>

        <FormInputText name="name" label="Product Name" />

        <FormInputColor
          name="color"
          label="Display Color"
        />

        <FormInputSelect
          :options="categoryOptions"
          label="Category"
          name="parentCategoryId"
        />

        <FormInputProductTags label="Tags" name="tagIds" />

        <div class="">
          <FormToggleSwitch
            name="hasVariablePrice"
            :options="variablePriceOptions"
            class="mb-2 max-w-48"
          />

          <FormInputPrice
            name="price"
            :disabled="values.hasVariablePrice"
          />
        </div>

        <template v-if="isVatEnabled">
          <FormInputVat name="vatRate" label="VAT" />
        </template>
      </BaseCard>

      <BaseCard class="flex-1">
        <FormInputProductModifierGroups
          label="Options"
          name="modifierGroupIds"
        />
      </BaseCard>
    </div>

    <div class="mt-2 flex justify-end">
      <FormSubmitButton :is-submitting="isSubmitting">
        Save Product
      </FormSubmitButton>
    </div>

    <BaseInnerLoading
      v-if="productId && isRetrievingProduct"
    />
  </form>

  <ErrorModal
    :is-open="isErrorModalOpen"
    message="Could not retrieve product"
    description="Something went wrong fetching product"
    :go-back-to="{
      name: 'productIndex',
    }"
    go-back-label="Return to product list"
  />
</template>

<script setup lang="ts">
import { ApiError } from "@/api"

const props = defineProps<{
  productId?: string
}>()

const { asOptions: categoryOptions, getById } =
  useCategories()

const router = useRouter()

const {
  handleSubmit,
  values,
  setValues,
  setErrors,
  isSubmitting,
} = useCreateUpdateProductForm()

const parentCategory = computed(
  () => getById(values.parentCategoryId).value
)
const onSubmit = handleSubmit(async (formValues) => {
  try {
    await (props.productId
      ? updateProduct(props.productId, { ...formValues })
      : createProduct(formValues))
    toast.success(
      props.productId
        ? "Product updated"
        : "Product created"
    )
    router.push({ name: "productIndex" })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.productId
        ? "Failed to update user"
        : "Failed to create user"
    )
    throw error
  }
})

const productPreviewColor = computed(
  () => values.color || parentCategory.value?.color
)

const productPreviewShortString = computed(() =>
  buildShortString(values.name ?? "")
)

const variablePriceOptions = [
  {
    value: false,
    label: "Fixed Price",
  },
  {
    value: true,
    label: "Variable Price",
  },
] as const

const { isVatEnabled } = useTenant()

const isErrorModalOpen = ref(false)
const isRetrievingProduct = ref(false)
onMounted(async () => {
  if (props.productId !== undefined) {
    try {
      isRetrievingProduct.value = true
      const product = await retrieveProduct(props.productId)
      setValues(product)
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isRetrievingProduct.value = false
    }
  }
})
</script>
